import type { StateAction } from "@entrilia/shared/types";
import { generateGuid } from "@entrilia/shared/utilities/generateGuid";
import type {
  Field,
  FieldIdToLookupReferences,
  FieldIdToValueMap,
  FieldsCitationsMap,
  FieldsConfidenceMap,
  LayoutRelatedObjects,
  RelatedObject,
  RelatedObjectsResponse,
  RelatedObjectsUpdateRequests,
} from "../../api/types/objectTypes";

export interface RelatedObjectValuesState {
  rowsByObjectType: Record<string, RelatedObjectRowModel[]>;
  dirtyObjectTypes: Set<string>;
  objectIdsToDeleteByObjectType: Record<string, Set<string>>;
}

export type RelatedObjectRowModel = {
  id: string;
  rowState: RowState;
} & FieldIdToValueMap;

interface RowState {
  lookupObjects: FieldIdToLookupReferences;
  fieldsConfidence: FieldsConfidenceMap;
  fieldsCitations: FieldsCitationsMap;
  updateStatus?: "new" | "updated";
}

export const getInitialRelatedObjectValuesState = (): RelatedObjectValuesState => ({
  rowsByObjectType: {},
  dirtyObjectTypes: new Set(),
  objectIdsToDeleteByObjectType: {},
});

const mapRelatedObjectsToRows = (objects: RelatedObject[]): RelatedObjectRowModel[] =>
  objects.map((object) => ({
    ...object.fieldValues,
    id: object.id,
    rowState: {
      lookupObjects: object.lookupObjects ?? {},
      fieldsConfidence: object.fieldsConfidence ?? {},
      fieldsCitations: object.fieldsCitations ?? {},
    },
  }));

export const getRelatedObjectFieldsByObjectType = (
  layouts: LayoutRelatedObjects[],
  relatedObjectResponses: RelatedObjectsResponse[]
): Record<string, Field[]> => {
  const fieldsByObjectType: Record<string, Field[]> = {};

  for (const layout of layouts) {
    const relatedObjectData = relatedObjectResponses.find((resp) => resp.objectType === layout.objectType);
    if (!relatedObjectData) {
      continue;
    }

    const fields = layout.fieldIds
      .map((fieldId) => relatedObjectData.objectClassDefinition.fields.find((f) => f.id === fieldId))
      .filter(Boolean) as Field[];

    fieldsByObjectType[layout.objectType] = fields;
  }

  return fieldsByObjectType;
};

export const getRelatedObjectsUpdateRequests = (
  state: RelatedObjectValuesState,
  relatedObjectLayouts: LayoutRelatedObjects[],
  parentObjectId: string
): RelatedObjectsUpdateRequests | undefined => {
  const result: RelatedObjectsUpdateRequests = {
    updatedObjects: [],
    deletedObjects: [],
    createdObjects: [],
  };

  for (const objectType of state.dirtyObjectTypes) {
    const rows = state.rowsByObjectType[objectType];
    if (!rows) {
      continue;
    }

    const layout = relatedObjectLayouts.find((layout) => layout.objectType === objectType);
    if (!layout) {
      continue;
    }

    const updatedRows = rows.filter((row) => row.rowState.updateStatus === "updated");
    const createdRows = rows.filter((row) => row.rowState.updateStatus === "new");
    const deletedRowIds = state.objectIdsToDeleteByObjectType[objectType] ?? [];

    for (const row of updatedRows) {
      const { id, rowState, ...fieldValues } = row;
      result.updatedObjects.push({
        objectType,
        objectId: id,
        fieldValuesUserInput: fieldValues,
      });
    }

    for (const row of createdRows) {
      const { id, rowState, ...fieldValues } = row;
      result.createdObjects.push({
        objectType,
        fieldValuesUserInput: { ...fieldValues, [layout.referenceFieldId]: parentObjectId },
      });
    }

    for (const id of deletedRowIds) {
      result.deletedObjects.push({
        objectType,
        objectId: id,
      });
    }
  }

  if (result.updatedObjects.length + result.createdObjects.length + result.deletedObjects.length === 0) {
    return undefined; // No changes to related objects
  }

  return result;
};

// Actions

export const initRowsAction =
  (relatedObjectsByType: RelatedObjectsResponse[]): StateAction<RelatedObjectValuesState> =>
  (state) => {
    const newRowsByObjectType: Record<string, RelatedObjectRowModel[]> = {};

    for (const relatedObjectsResp of relatedObjectsByType) {
      const rows = mapRelatedObjectsToRows(relatedObjectsResp.relatedObjects);
      newRowsByObjectType[relatedObjectsResp.objectType] = rows;
    }

    return {
      ...state,
      rowsByObjectType: newRowsByObjectType,
      dirtyObjectTypes: new Set(),
      objectIdsToDeleteByObjectType: {},
    };
  };

export const updateRowAction =
  (objectType: string, updatedRow: RelatedObjectRowModel): StateAction<RelatedObjectValuesState> =>
  (state) => {
    const rows = state.rowsByObjectType[objectType];
    if (!rows) {
      return state;
    }

    const updatedRows: RelatedObjectRowModel[] = rows.map((row) =>
      row.id === updatedRow.id
        ? {
            ...updatedRow,
            rowState: {
              ...row.rowState,
              updateStatus: row.rowState.updateStatus === "new" ? "new" : "updated",
            },
          }
        : row
    );

    return {
      ...state,
      rowsByObjectType: { ...state.rowsByObjectType, [objectType]: updatedRows },
      dirtyObjectTypes: new Set([...state.dirtyObjectTypes, objectType]),
    };
  };

export const deleteRowAction =
  (objectType: string, rowId: string): StateAction<RelatedObjectValuesState> =>
  (state) => {
    const rows = state.rowsByObjectType[objectType];
    if (!rows) {
      return state;
    }

    const updatedRows: RelatedObjectRowModel[] = rows.filter((row) => row.id !== rowId);

    const rowToDelete = rows.find((row) => row.id === rowId);
    if (!rowToDelete) {
      return state;
    }

    const objectIdsToDeleteByObjectType =
      rowToDelete.rowState.updateStatus === "new"
        ? state.objectIdsToDeleteByObjectType
        : {
            ...state.objectIdsToDeleteByObjectType,
            [objectType]: new Set([...(state.objectIdsToDeleteByObjectType[objectType] ?? []), rowId]),
          };

    return {
      ...state,
      rowsByObjectType: { ...state.rowsByObjectType, [objectType]: updatedRows },
      dirtyObjectTypes: new Set([...state.dirtyObjectTypes, objectType]),
      objectIdsToDeleteByObjectType,
    };
  };

export const addRowAction =
  (objectType: string): StateAction<RelatedObjectValuesState> =>
  (state) => {
    const rows = state.rowsByObjectType[objectType];
    if (!rows) {
      return state;
    }

    const newRow: RelatedObjectRowModel = {
      id: `new_${generateGuid()}`,
      rowState: {
        lookupObjects: {},
        fieldsConfidence: {},
        fieldsCitations: {},
        updateStatus: "new",
      },
    };

    const updatedRows: RelatedObjectRowModel[] = [...rows, newRow];

    return {
      ...state,
      rowsByObjectType: { ...state.rowsByObjectType, [objectType]: updatedRows },
      dirtyObjectTypes: new Set([...state.dirtyObjectTypes, objectType]),
    };
  };
